'use client'

import { useEffect, useRef, useState } from 'react'

// House-style particle text: the phrase is sampled into a grid of gold pixel
// dots that scatter in and coalesce into the words on load, then hold static
// (no continuous rAF — assembles once, then stops, so it never taxes the
// page). The real text stays in the DOM for SEO + screen readers; the canvas
// is decorative and only takes over once fonts are ready and motion is
// allowed. Reduced-motion / no-canvas → the plain gold text, no animation.
//
// This is the hand-built equivalent of the reactbits-pro particle-text
// component (that registry needs a license key not present in this env). To
// swap in the official one later: add REACTBITS_LICENSE_KEY, run
// `shadcn add @reactbits-pro/<name>`, and replace <ParticleText/> below.

const GOLD = '#C9A84C'

export function ParticleText({ text, className = '' }) {
  const textRef = useRef(null)
  const canvasRef = useRef(null)
  const rafRef = useRef(0)
  const [canvasActive, setCanvasActive] = useState(false)

  useEffect(() => {
    const textEl = textRef.current
    const canvas = canvasRef.current
    if (!textEl || !canvas) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let cancelled = false
    let built = false

    const build = () => {
      if (cancelled || built) return
      const rect = textEl.getBoundingClientRect()
      if (rect.width < 4 || rect.height < 4) return
      built = true
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      const w = Math.ceil(rect.width * dpr)
      const h = Math.ceil(rect.height * dpr)

      canvas.width = w
      canvas.height = h
      canvas.style.width = `${rect.width}px`
      canvas.style.height = `${rect.height}px`

      // Draw the phrase once to read its shape.
      const cs = getComputedStyle(textEl)
      ctx.clearRect(0, 0, w, h)
      ctx.fillStyle = '#fff'
      ctx.textBaseline = 'middle'
      ctx.textAlign = 'left'
      ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${parseFloat(cs.fontSize) * dpr}px ${cs.fontFamily}`
      ctx.fillText(text, 0, h / 2)

      const { data } = ctx.getImageData(0, 0, w, h)
      const step = Math.max(3, Math.round(4 * dpr))
      const targets = []
      for (let y = 0; y < h; y += step) {
        for (let x = 0; x < w; x += step) {
          if (data[(y * w + x) * 4 + 3] > 130) targets.push({ x, y })
        }
      }
      if (targets.length === 0) return

      // Each dot flies in from a scattered start toward its place in the word.
      const dotR = Math.max(1, 1.1 * dpr)
      const particles = targets.map((t) => ({
        x: Math.random() * w,
        y: Math.random() < 0.5 ? -h * Math.random() : h + h * Math.random(),
        tx: t.x,
        ty: t.y,
        o: 0.55 + Math.random() * 0.45,
      }))

      setCanvasActive(true)

      const tick = () => {
        if (cancelled) return
        ctx.clearRect(0, 0, w, h)
        let maxDelta = 0
        for (const p of particles) {
          p.x += (p.tx - p.x) * 0.12
          p.y += (p.ty - p.y) * 0.12
          maxDelta = Math.max(maxDelta, Math.abs(p.tx - p.x), Math.abs(p.ty - p.y))
          ctx.globalAlpha = p.o
          ctx.fillStyle = GOLD
          ctx.beginPath()
          ctx.arc(p.x, p.y, dotR, 0, Math.PI * 2)
          ctx.fill()
        }
        ctx.globalAlpha = 1
        if (maxDelta < 0.4) {
          // Settled — snap crisp and stop (no ongoing animation cost).
          ctx.clearRect(0, 0, w, h)
          ctx.fillStyle = GOLD
          for (const p of particles) {
            ctx.globalAlpha = p.o
            ctx.beginPath()
            ctx.arc(p.tx, p.ty, dotR, 0, Math.PI * 2)
            ctx.fill()
          }
          ctx.globalAlpha = 1
          return
        }
        rafRef.current = requestAnimationFrame(tick)
      }
      rafRef.current = requestAnimationFrame(tick)
    }

    // Trigger from several angles so it never silently no-ops: next frame
    // (layout settled), and once the real webfont resolves (right glyphs).
    // The `built` guard makes these idempotent — whichever lands first wins.
    requestAnimationFrame(() => build())
    if (document.fonts?.ready) document.fonts.ready.then(() => build())

    // Re-sample on resize (rebuild from scattered again).
    let resizeTimer
    const onResize = () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        cancelAnimationFrame(rafRef.current)
        built = false
        build()
      }, 200)
    }
    window.addEventListener('resize', onResize)

    return () => {
      cancelled = true
      cancelAnimationFrame(rafRef.current)
      clearTimeout(resizeTimer)
      window.removeEventListener('resize', onResize)
    }
  }, [text])

  return (
    <span className={`relative inline-block align-baseline ${className}`}>
      {/* Real text — SEO + screen readers always read it. Hidden visually
          only once the canvas has taken over. */}
      <span
        ref={textRef}
        className="whitespace-nowrap text-kingdom-gold transition-opacity duration-300"
        style={{ opacity: canvasActive ? 0 : 1 }}
      >
        {text}
      </span>
      <canvas
        ref={canvasRef}
        aria-hidden
        className="pointer-events-none absolute top-0 left-0 transition-opacity duration-500"
        style={{ opacity: canvasActive ? 1 : 0 }}
      />
    </span>
  )
}
